import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  Search,
  X,
  LayoutGrid,
  List,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { SongCard } from '../../components/song/SongCard.js';
import { ArtistCard } from '../../components/artist/ArtistCard.js';
import { AlbumCard } from '../../components/song/AlbumCard.js';
import { songsService } from '../../services/songs.service.js';
import { artistsService } from '../../services/artists.service.js';
import { genresService } from '../../services/genres.service.js';

type Tab = 'songs' | 'artists' | 'albums';
type ViewMode = 'grid' | 'list';

function ResultsSkeleton({ count = 8, view = 'grid' }: { count?: number; view?: ViewMode }) {
  if (view === 'list') {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
            <div className="w-14 h-14 rounded-lg bg-surface-hover shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-surface-hover rounded w-1/2" />
              <div className="h-3 bg-surface-hover rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl overflow-hidden bg-surface animate-pulse">
          <div className="aspect-square bg-surface-hover" />
          <div className="p-3 space-y-2">
            <div className="h-4 bg-surface-hover rounded w-3/4" />
            <div className="h-3 bg-surface-hover rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const initialTab = (searchParams.get('tab') as Tab) ?? 'songs';
  const initialPage = Number(searchParams.get('page') ?? '1');

  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [page, setPage] = useState(initialPage);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const updateSearchParams = useCallback(
    (q: string, tab: Tab, p: number) => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (tab !== 'songs') params.set('tab', tab);
      if (p > 1) params.set('page', String(p));
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    updateSearchParams(query, activeTab, page);
  }, [query, activeTab, page, updateSearchParams]);

  const { data: genresData } = useQuery({
    queryKey: ['genres'],
    queryFn: () => genresService.getGenres(),
  });

  const { data: songsData, isLoading: songsLoading } = useQuery({
    queryKey: ['search', 'songs', query, page, selectedGenres, selectedLanguages, yearFrom, yearTo],
    queryFn: () =>
      songsService.search({
        q: query,
        page,
        limit: 12,
        genres: selectedGenres.length ? selectedGenres.join(',') : undefined,
        languages: selectedLanguages.length ? selectedLanguages.join(',') : undefined,
        yearFrom: yearFrom ? Number(yearFrom) : undefined,
        yearTo: yearTo ? Number(yearTo) : undefined,
      } as any),
    enabled: activeTab === 'songs',
  });

  const { data: artistsData, isLoading: artistsLoading } = useQuery({
    queryKey: ['search', 'artists', query, page],
    queryFn: () => artistsService.getArtists({ q: query, page, limit: 12 } as any),
    enabled: activeTab === 'artists',
  });

  const { data: albumsData, isLoading: albumsLoading } = useQuery({
    queryKey: ['search', 'albums', query, page],
    queryFn: () =>
      import('../../services/albums.service.js').then((m) =>
        m.albumsService.getAlbums({ q: query, page, limit: 12 } as any),
      ),
    enabled: activeTab === 'albums',
  });

  const songs = songsData?.data ?? [];
  const artists = artistsData?.data ?? [];
  const albums = albumsData?.data ?? [];
  const genres = genresData?.data ?? [];

  const songsMeta = (songsData as any)?.meta;
  const totalPages = songsMeta?.totalPages ?? 1;

  const LANGUAGES = [
    { code: 'es', name: 'Espanol' },
    { code: 'en', name: 'Ingles' },
    { code: 'pt', name: 'Portugues' },
    { code: 'fr', name: 'Frances' },
  ];

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
  }

  function clearSearch() {
    setQuery('');
    setPage(1);
  }

  function toggleGenre(slug: string) {
    setSelectedGenres((prev) =>
      prev.includes(slug) ? prev.filter((g) => g !== slug) : [...prev, slug],
    );
    setPage(1);
  }

  function toggleLanguage(code: string) {
    setSelectedLanguages((prev) =>
      prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code],
    );
    setPage(1);
  }

  function clearFilters() {
    setSelectedGenres([]);
    setSelectedLanguages([]);
    setYearFrom('');
    setYearTo('');
    setPage(1);
  }

  const isLoading =
    activeTab === 'songs' ? songsLoading : activeTab === 'artists' ? artistsLoading : albumsLoading;

  return (
    <>
      <Helmet>
        <title>{query ? `"${query}" - Busqueda` : 'Buscar'} | LyricsHub</title>
        <meta name="description" content={`Resultados de busqueda para "${query}" en LyricsHub`} />
      </Helmet>

      {/* Search Input */}
      <form onSubmit={handleSearch} className="relative mb-6">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar canciones, artistas, albums..."
          className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-base"
          autoFocus
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={18} />
          </button>
        )}
      </form>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-6 border-b border-border">
        <div className="flex gap-1">
          {[
            { key: 'songs' as Tab, label: 'Canciones', count: songsMeta?.total },
            { key: 'artists' as Tab, label: 'Artistas', count: (artistsData as any)?.meta?.total },
            { key: 'albums' as Tab, label: 'Albums', count: (albumsData as any)?.meta?.total },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(1);
              }}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 text-xs text-text-muted">({tab.count})</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-1">
          {/* Mobile filter toggle */}
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-surface-hover text-text-muted transition-colors"
          >
            <SlidersHorizontal size={18} />
          </button>
          {activeTab === 'songs' && (
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text-primary'}`}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 ${viewMode === 'list' ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text-primary'}`}
              >
                <List size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Filters */}
        <aside
          className={`${
            filtersOpen ? 'block' : 'hidden'
          } md:block w-full md:w-56 shrink-0 space-y-6`}
        >
          {/* Genre filters */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2">Genero</h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {genres.slice(0, 12).map((genre: any) => (
                <label key={genre.id} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedGenres.includes(genre.slug)}
                    onChange={() => toggleGenre(genre.slug)}
                    className="rounded border-border text-primary focus:ring-primary/50"
                  />
                  <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                    {genre.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Language filters */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2">Idioma</h3>
            <div className="space-y-1.5">
              {LANGUAGES.map((lang) => (
                <label key={lang.code} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedLanguages.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                    className="rounded border-border text-primary focus:ring-primary/50"
                  />
                  <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                    {lang.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Year range */}
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2">Ano</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={yearFrom}
                onChange={(e) => {
                  setYearFrom(e.target.value);
                  setPage(1);
                }}
                placeholder="Desde"
                min="1950"
                max="2026"
                className="w-full px-2 py-1.5 text-sm rounded-lg bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
              <span className="text-text-muted text-xs">-</span>
              <input
                type="number"
                value={yearTo}
                onChange={(e) => {
                  setYearTo(e.target.value);
                  setPage(1);
                }}
                placeholder="Hasta"
                min="1950"
                max="2026"
                className="w-full px-2 py-1.5 text-sm rounded-lg bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>

          <button
            onClick={clearFilters}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            Limpiar filtros
          </button>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <ResultsSkeleton count={8} view={viewMode} />
          ) : activeTab === 'songs' ? (
            <>
              {songs.length === 0 ? (
                <div className="text-center py-12 text-text-muted">
                  <Search size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="text-lg">No se encontraron resultados</p>
                  <p className="text-sm mt-1">Intenta con otros terminos de busqueda</p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {songs.map((song: any) => (
                    <SongCard
                      key={song.id}
                      slug={song.slug}
                      title={song.title}
                      artistName={song.artistName}
                      artistSlug={song.artistSlug}
                      coverUrl={song.coverUrl}
                      genreName={song.genreName}
                      viewCount={song.viewCount}
                      variant="vertical"
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {songs.map((song: any) => (
                    <SongCard
                      key={song.id}
                      slug={song.slug}
                      title={song.title}
                      artistName={song.artistName}
                      artistSlug={song.artistSlug}
                      coverUrl={song.coverUrl}
                      genreName={song.genreName}
                      viewCount={song.viewCount}
                      variant="horizontal"
                    />
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'artists' ? (
            artists.length === 0 ? (
              <div className="text-center py-12 text-text-muted">
                <p className="text-lg">No se encontraron artistas</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {artists.map((artist: any) => (
                  <ArtistCard
                    key={artist.id}
                    slug={artist.slug}
                    name={artist.name}
                    avatarUrl={artist.avatarUrl}
                    isVerified={artist.isVerified}
                    songCount={artist.songCount}
                  />
                ))}
              </div>
            )
          ) : albums.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <p className="text-lg">No se encontraron albums</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {albums.map((album: any) => (
                <AlbumCard
                  key={album.id}
                  slug={album.slug}
                  title={album.title}
                  artistName={album.artistName}
                  artistSlug={album.artistSlug}
                  coverUrl={album.coverUrl}
                  releaseYear={album.releaseYear}
                  songCount={album.songCount}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && activeTab === 'songs' && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      page === pageNum
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
